import {
  useState, useCallback, useEffect, useMemo, useRef,
} from 'react'
import type { ApolloClient } from '@apollo/client'
import { print } from 'graphql'
import { APOLLO_CLIENT_ERROR_POLICY_ALL } from '../../../apollo-graphql/index'
import { FOLLOW_PLAYLIST, UNFOLLOW_PLAYLIST } from '../../query'
import useLatest from '../../shared/use-latest'
import type { PlaylistSearchNode, UnfollowPlaylistResponse } from '../../types'

// Inline type to avoid circular dependency with ProfileCollectionsTabs
type CollectionsTab = 'myPlaylists' | 'following' | 'discover'

/**
 * Result type used by Oracle search hooks to represent a discovered playlist.
 * Only the fields consumed by seeding logic are required here.
 */
interface OracleResultPlaylist {
  id: string
  isFollowing?: boolean | null
  followerCount?: number | null
}

interface OracleSearchResults {
  playlists: Array<{ playlist?: OracleResultPlaylist | null }>
}

export interface UsePlaylistFollowMutationsParams {
  jwt: string | undefined
  apolloClient: ApolloClient<object>
  followedPlaylists: PlaylistSearchNode[]
  activeResults: OracleSearchResults
  removeFollowedPlaylist: ((id: string) => void) | null
  refetchFollowedPlaylists: (() => void) | null
  /** Called when a follow/unfollow mutation fails and the optimistic update is reverted. */
  onMutationError?: () => void
}

export interface UsePlaylistFollowMutationsReturn {
  followedIds: Set<string>
  followerCounts: Record<string, number>
  unfollowToast: { playlistId: string; playlistName: string } | null
  handleFollowPlaylist: (id: string) => Promise<void>
  handleUnfollowDiscover: (id: string, name: string) => void
  handleUndoDiscoverUnfollow: (id: string) => void
  handleDismissDiscoverUnfollow: (id: string) => Promise<void>
  handleUnfollowFollowing: (id: string, name: string) => void
  flushPendingUnfollow: (currentTab: CollectionsTab) => void
  setUnfollowToast: React.Dispatch<React.SetStateAction<{
    playlistId: string; playlistName: string
  } | null>>
}

// Pre-serialized at module scope for sendBeacon/keepalive on beforeunload.
// This avoids re-serializing the AST on every page unload event.
const UNFOLLOW_PLAYLIST_QUERY = print(UNFOLLOW_PLAYLIST)

const TABS_DISCOVER = 'discover'
const TABS_FOLLOWING = 'following'

export default function usePlaylistFollowMutations ({
  jwt,
  apolloClient,
  followedPlaylists,
  activeResults,
  removeFollowedPlaylist,
  refetchFollowedPlaylists,
  onMutationError,
}: UsePlaylistFollowMutationsParams): UsePlaylistFollowMutationsReturn {
  const [followedIds, setFollowedIds] = useState<Set<string>>(new Set())
  const [followerCounts, setFollowerCounts] = useState<Record<string, number>>({})
  const [unfollowToast, setUnfollowToast] = useState<{
    playlistId: string; playlistName: string
  } | null>(null)
  // State/prop mirrors read by the unmount + beforeunload cleanup effects.
  // useLatest commits the write in a post-commit effect, so a
  // Suspense-discarded render cannot leave the ref pointing at a value that
  // was never actually rendered.  Handlers still mutate these refs eagerly
  // (see handleUnfollow*, handleDismiss* below) — that is intentional and
  // stays as-is, because those mutations happen in event handlers, not
  // during render.
  const unfollowToastRef = useLatest(unfollowToast)

  // Track local follow/unfollow mutations so the seeding effect doesn't overwrite them
  const localMutationsRef = useRef<Map<string, 'added' | 'removed'>>(new Map())
  // Track mutation-returned follower counts so the seeding effect doesn't overwrite them
  const localCountsRef = useRef<Record<string, number>>({})
  // Track playlist IDs whose follower count was unknown (null) before an optimistic follow.
  // Used by revertFollow to delete the key entirely rather than decrement to 0.
  const unknownCountIdsRef = useRef<Set<string>>(new Set())
  // Refs for cleanup effect — must reflect the latest committed props, not
  // a value written during a render that may be discarded under Suspense.
  const apolloClientRef = useLatest(apolloClient)
  const jwtRef = useLatest(jwt)

  // Mirror followerCounts in a ref so callbacks can read the latest value
  // without capturing stale state.  Commit-phase via useLatest keeps this
  // consistent with Suspense-safe reads from cleanup effects.
  const followerCountsRef = useLatest(followerCounts)

  // Guard async callbacks against state updates after unmount
  const isMountedRef = useRef(true)
  useEffect(() => () => { isMountedRef.current = false }, [])

  // Helpers to reduce repetition in follow/unfollow handlers
  const applyServerCount = useCallback((playlistId: string, count: number | null | undefined) => {
    if (count != null) {
      localCountsRef.current[playlistId] = count
      setFollowerCounts((prev) => ({ ...prev, [playlistId]: count }))
    }
  }, [])

  const revertFollow = useCallback((playlistId: string) => {
    // Capture intervening-unfollow state BEFORE clearing localMutationsRef.
    const supersededByUnfollow = localMutationsRef.current.get(playlistId) === 'removed'
    localMutationsRef.current.delete(playlistId)
    delete localCountsRef.current[playlistId]
    setFollowedIds((prev) => {
      const next = new Set(prev)
      next.delete(playlistId)
      return next
    })
    if (supersededByUnfollow) {
      // A later unfollow already wrote the authoritative count (e.g. 0).
      // The failed follow's revert must leave it intact — do not delete the
      // key and do not decrement again.
      unknownCountIdsRef.current.delete(playlistId)
      return
    }
    if (unknownCountIdsRef.current.has(playlistId)) {
      // Count was unknown before the optimistic follow — remove the key entirely
      // rather than leaving a synthetic 0 that never existed server-side.
      unknownCountIdsRef.current.delete(playlistId)
      setFollowerCounts((prev) => {
        const next = { ...prev }
        delete next[playlistId]
        return next
      })
    } else {
      setFollowerCounts((prev) => {
        const reverted = (prev[playlistId] ?? 1) - 1
        return { ...prev, [playlistId]: reverted }
      })
    }
  }, [])

  // Revert an optimistic Discover unfollow back to the followed state when the
  // server-side unfollow did not succeed.  Extracted so both the
  // resolved-but-no-payload branch and the network/transport catch share one
  // implementation.  Callers must ensure the component is still mounted.
  const revertUnfollow = useCallback((playlistId: string) => {
    localMutationsRef.current.set(playlistId, 'added')
    setFollowedIds((prev) => new Set(prev).add(playlistId))
    setFollowerCounts((prev) => {
      const restored = (prev[playlistId] ?? 0) + 1
      localCountsRef.current[playlistId] = restored
      return { ...prev, [playlistId]: restored }
    })
    onMutationError?.()
  }, [onMutationError])

  // Seed followedIds from followed playlists and Oracle isFollowing data;
  // seed followerCounts from Oracle results.
  // Apply local mutations on top so optimistic UI survives server re-seeds.
  // Self-clean entries where server data has caught up with local mutations.
  //
  // Depend on content SIGNATURES (string-encoded snapshots of the exact
  // fields the body reads) rather than the array references
  // themselves. Both `followedPlaylists` (from useFollowedPlaylists) and
  // `activeResults.playlists` (from useDiscoverTabState → useAiSearchStream)
  // produce fresh array references on every state transition even when
  // their semantic content is unchanged — that was re-firing this seed
  // effect on every Discover-tab stream tick and Apollo broadcast, churning
  // setFollowedIds + setFollowerCounts and cascading into row re-renders.
  // The body reads ONLY `.id` from `followedPlaylists` and `.id` /
  // `.isFollowing` / `.followerCount` from `activeResults.playlists[*].playlist`,
  // so the signatures below capture exactly that surface.  Re-verify and
  // extend the signature if the effect body adds new field reads.
  const followedIdsSig = useMemo(
    () => followedPlaylists.map((p) => p.id).join(','),
    [followedPlaylists],
  )
  const activeResultsSig = useMemo(
    () => activeResults.playlists
      .map((r) => {
        const pl = r.playlist
        if (!pl) return ''
        return `${pl.id}|${pl.isFollowing ? 1 : 0}|${pl.followerCount ?? ''}`
      })
      .join(','),
    [activeResults.playlists],
  )
  const followedPlaylistsRef = useLatest(followedPlaylists)
  const activeResultsPlaylistsRef = useLatest(activeResults.playlists)
  useEffect(() => {
    const fp = followedPlaylistsRef.current
    const ap = activeResultsPlaylistsRef.current
    const ids = new Set(fp.map((p) => p.id))
    const counts: Record<string, number> = {}
    ap.forEach((result) => {
      if (!result.playlist) return
      if (result.playlist.isFollowing) ids.add(result.playlist.id)
      if (result.playlist.followerCount != null) {
        counts[result.playlist.id] = result.playlist.followerCount
      }
    })
    // Single pass: prune converged mutations, then layer remaining on top of server data.
    // Preserve server-confirmed counts in localCountsRef so stale oracle data
    // doesn't overwrite them — only delete counts that came from optimistic updates
    // (i.e., where the oracle count already matches the local count).
    localMutationsRef.current.forEach((action, playlistId) => {
      const serverHasIt = ids.has(playlistId)
      if (
        (action === 'added' && serverHasIt)
        || (action === 'removed' && !serverHasIt)
      ) {
        localMutationsRef.current.delete(playlistId)
        // Only discard localCountsRef if oracle already agrees with it;
        // otherwise keep it so the server-confirmed count survives the merge.
        const oracleCount = counts[playlistId]
        const localCount = localCountsRef.current[playlistId]
        if (oracleCount != null && localCount != null && oracleCount === localCount) {
          delete localCountsRef.current[playlistId]
        }
      } else if (action === 'added') {
        ids.add(playlistId)
      } else if (action === 'removed') {
        ids.delete(playlistId)
      }
    })
    // Merge local mutation-returned counts on top of oracle counts
    const mergedCounts = { ...counts, ...localCountsRef.current }
    setFollowedIds(ids)
    setFollowerCounts(mergedCounts)
    // Signatures are the deliberate dep surface; the underlying arrays are
    // read via useLatest refs at fire-time so we don't see stale closures.
  }, [followedIdsSig, activeResultsSig])

  // On unmount, fire-and-forget any pending Discover unfollow mutation
  // so it isn't lost when navigating away (SPA navigation)
  useEffect(() => () => {
    const pending = unfollowToastRef.current
    if (pending) {
      apolloClientRef.current.mutate({
        mutation: UNFOLLOW_PLAYLIST,
        variables: { playlistId: pending.playlistId },
        context: { jwt: jwtRef.current },
      }).catch(() => { /* best-effort cleanup on unmount */ })
    }
  }, [])

  // On full browser refresh/close, use fetch with keepalive to persist pending unfollow.
  // navigator.sendBeacon can't set auth headers, so we use fetch keepalive instead.
  useEffect(() => {
    const handleBeforeUnload = () => {
      const pending = unfollowToastRef.current
      if (!pending) return
      const token = jwtRef.current
      if (!token) return
      fetch('/graphql', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          query: UNFOLLOW_PLAYLIST_QUERY,
          variables: { playlistId: pending.playlistId },
        }),
        keepalive: true,
      }).catch(() => { /* best-effort cleanup on beforeunload */ })
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [])

  const handleDismissDiscoverUnfollow = useCallback(async (playlistId: string) => {
    // Eagerly clear ref so unmount/beforeunload handlers don't re-fire
    unfollowToastRef.current = null
    setUnfollowToast((prev) => (prev?.playlistId === playlistId ? null : prev))
    try {
      // errorPolicy 'all' (consistent with the rest of Community): a 200 that
      // carries data plus a non-fatal `errors[]` (common with federated /
      // @auth subfields) resolves instead of throwing, so a server-confirmed
      // unfollow is not spuriously reverted.  Success is keyed off the
      // returned playlist payload, not the absence of errors.
      const { data } = await apolloClient.mutate<UnfollowPlaylistResponse>({
        mutation: UNFOLLOW_PLAYLIST,
        variables: { playlistId },
        context: { jwt },
        errorPolicy: APOLLO_CLIENT_ERROR_POLICY_ALL,
      })
      if (!isMountedRef.current) return
      if (!data?.unfollowPlaylist) {
        // Resolved without a playlist payload — treat as a failed unfollow.
        revertUnfollow(playlistId)
        return
      }
      applyServerCount(playlistId, data.unfollowPlaylist.followerCount)
      if (refetchFollowedPlaylists) {
        // Best-effort background refresh; a failed refetch must not surface
        // as an unhandled rejection (mirrors useFollowedPlaylists).
        Promise.resolve(refetchFollowedPlaylists()).catch(() => {
          /* leave current state in place if the refetch fails */
        })
      }
    } catch {
      // Network/transport failure — revert to followed state so the UI stays
      // in sync with the server.  Guard against state updates after unmount
      // (component may have been removed while the mutation was in flight).
      if (!isMountedRef.current) return
      revertUnfollow(playlistId)
    }
  }, [apolloClient, jwt, refetchFollowedPlaylists, applyServerCount, revertUnfollow])

  const handleFollowPlaylist = useCallback(async (playlistId: string) => {
    // Optimistic UI: show follow state and increment count immediately.
    // Read current count synchronously (before queueing state updates) so
    // unknownCountIdsRef is updated before revertFollow could be called.
    if (followerCountsRef.current[playlistId] == null) {
      unknownCountIdsRef.current.add(playlistId)
    }
    localMutationsRef.current.set(playlistId, 'added')
    setFollowedIds((prev) => new Set(prev).add(playlistId))
    setFollowerCounts((prev) => {
      const optimistic = (prev[playlistId] ?? 0) + 1
      localCountsRef.current[playlistId] = optimistic
      return { ...prev, [playlistId]: optimistic }
    })
    try {
      const { data } = await apolloClient.mutate({
        mutation: FOLLOW_PLAYLIST,
        variables: { playlistId },
        context: { jwt },
      })
      if (!isMountedRef.current) return
      applyServerCount(playlistId, data?.followPlaylist?.followerCount)
      if (refetchFollowedPlaylists) {
        refetchFollowedPlaylists()
      }
    } catch {
      // Follow failed — revert optimistic update
      revertFollow(playlistId)
      onMutationError?.()
    }
  }, [apolloClient, jwt, refetchFollowedPlaylists, applyServerCount, revertFollow, onMutationError])

  const handleUnfollowDiscover = useCallback((playlistId: string, playlistName: string) => {
    // Flush any pending unfollow mutation before starting a new one so rapid
    // unfollows don't silently drop mutations (the previous toast would be
    // overwritten and its dismiss handler — which fires the mutation — lost).
    const pending = unfollowToastRef.current
    if (pending) {
      if (removeFollowedPlaylist) {
        removeFollowedPlaylist(pending.playlistId)
      } else {
        // Fallback: direct mutation if prop unavailable
        apolloClient.mutate({
          mutation: UNFOLLOW_PLAYLIST,
          variables: { playlistId: pending.playlistId },
          context: { jwt },
        }).catch(() => { /* fire-and-forget fallback */ })
      }
    }

    localMutationsRef.current.set(playlistId, 'removed')
    // An explicit unfollow makes any earlier "count was unknown" marker
    // stale; clearing it stops a late-failing follow's revert from
    // discarding this authoritative post-unfollow count.
    unknownCountIdsRef.current.delete(playlistId)
    setFollowedIds((prev) => {
      const next = new Set(prev)
      next.delete(playlistId)
      return next
    })
    setFollowerCounts((prev) => {
      const decremented = Math.max((prev[playlistId] ?? 1) - 1, 0)
      localCountsRef.current[playlistId] = decremented
      return { ...prev, [playlistId]: decremented }
    })
    // Eagerly update ref before setState so unmount/beforeunload handlers
    // always see the latest pending unfollow even if navigation preempts render
    unfollowToastRef.current = { playlistId, playlistName }
    setUnfollowToast({ playlistId, playlistName })
  }, [apolloClient, jwt, removeFollowedPlaylist])

  const handleUndoDiscoverUnfollow = useCallback((playlistId: string) => {
    // No mutation needed — the unfollow was deferred and never sent to the
    // server.  Restoring optimistic state is sufficient.
    localMutationsRef.current.set(playlistId, 'added')
    setFollowedIds((prev) => new Set(prev).add(playlistId))
    setFollowerCounts((prev) => {
      const optimistic = (prev[playlistId] ?? 0) + 1
      localCountsRef.current[playlistId] = optimistic
      return { ...prev, [playlistId]: optimistic }
    })
    // Eagerly clear ref so unmount/beforeunload handlers don't re-fire
    unfollowToastRef.current = null
    setUnfollowToast((prev) => (prev?.playlistId === playlistId ? null : prev))
  }, [])

  const handleUnfollowFollowing = useCallback((playlistId: string, playlistName: string) => {
    if (unfollowToastRef.current && removeFollowedPlaylist) {
      removeFollowedPlaylist(unfollowToastRef.current.playlistId)
    }

    // Eagerly update ref before setState so unmount/beforeunload handlers
    // always see the latest pending unfollow even if navigation preempts render
    unfollowToastRef.current = { playlistId, playlistName }
    setUnfollowToast({ playlistId, playlistName })
  }, [removeFollowedPlaylist])

  const flushPendingUnfollow = useCallback((currentTab: CollectionsTab) => {
    const pending = unfollowToastRef.current
    if (!pending) return
    if (currentTab === TABS_DISCOVER) {
      // Discover tab: fire the actual UNFOLLOW_PLAYLIST mutation
      handleDismissDiscoverUnfollow(pending.playlistId)
    } else if (currentTab === TABS_FOLLOWING && removeFollowedPlaylist) {
      // Following tab: confirm removal
      removeFollowedPlaylist(pending.playlistId)
      setUnfollowToast((prev) => (prev?.playlistId === pending.playlistId ? null : prev))
    }
  }, [handleDismissDiscoverUnfollow, removeFollowedPlaylist])

  // Memoize the return object so consumers receive a stable reference when
  // none of the underlying state/callbacks have changed.  `setUnfollowToast`
  // is a React setState dispatch and is guaranteed stable, so it is omitted
  // from the dep list to keep it tight.
  return useMemo(() => ({
    followedIds,
    followerCounts,
    unfollowToast,
    handleFollowPlaylist,
    handleUnfollowDiscover,
    handleUndoDiscoverUnfollow,
    handleDismissDiscoverUnfollow,
    handleUnfollowFollowing,
    flushPendingUnfollow,
    setUnfollowToast,
  }), [
    followedIds,
    followerCounts,
    unfollowToast,
    handleFollowPlaylist,
    handleUnfollowDiscover,
    handleUndoDiscoverUnfollow,
    handleDismissDiscoverUnfollow,
    handleUnfollowFollowing,
    flushPendingUnfollow,
  ])
}
